import { useCallback } from 'react';

import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import styles from '@/components/organisms/Dialogs/Action/Action/Action.module.scss';
import ActionHeader from '@/components/organisms/Dialogs/ActionHeader/ActionHeader';
import Box from '@/components/organisms/Dialogs/Box/Box';
import Placeholder from '@/components/organisms/Dialogs/Node/RequisitePlaceholder';
import useMenu from '@/hooks/useMenu';
import { cn } from '@/lib/utils';
import { selectHasError, selectHasWarning } from '@/redux/dialogAlerts/selectors';
import { TYPES, generateRequisite } from '@/redux/dialogs/helper';
import { addNewRequisite, moveRequisite, selectRequisite } from '@/redux/nodes/actions';
import { selectIsIdInFlow, selectIsNodeConditionRequisiteSelected } from '@/redux/nodes/selectors';
import { Requisite as RequisiteType } from '@/shared/types/node';
import { RootState } from '@/shared/types/state';
import { isKeyEnter, preventClickThrough } from '@/util/util';

import { BoxErrorIcon, BoxWarningIcon } from '../Action/Action/Action';

interface Props {
  requisite: RequisiteType;
  index: number;
  nodeId: string;
  requisiteIndex?: number;
}

const type = TYPES.QUESTION;

const Requisite = ({ requisite, index, nodeId }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { deleteRequisite } = useMenu();
  const hasDialogError = useSelector(selectHasError(requisite.requisite_id));
  const hasDialogWarning = useSelector(selectHasWarning(requisite.requisite_id));

  const [_, dragRef] = useDrag({
    item: { nodeId, requisiteIndex: index, move: true, subType: TYPES.QUESTION },
    type: TYPES.QUESTION,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const { selectedRequisiteIndex, selectedNodeId } = useSelector(
    (state: RootState) => ({
      selectedRequisiteIndex: state.nodes.selectedRequisiteIndex,
      selectedNodeId: state.nodes.selectedNodeId,
    }),
    shallowEqual
  );

  const isActionInFlow = useSelector(selectIsIdInFlow(requisite.requisite_id));
  const isAnythingSelected = useSelector(selectIsNodeConditionRequisiteSelected);

  const dropRequisiteInNode = useCallback(
    (item) => {
      if (item.move) {
        return dispatch(moveRequisite({ source: item, target: { nodeId, index } }));
      }

      return dispatch(
        addNewRequisite({ nodeId, requisite: generateRequisite(), requisiteIndex: index })
      );
    },
    [dispatch, index, nodeId]
  );

  const onClick = useCallback(
    (e) => {
      preventClickThrough(e);
      dispatch(selectRequisite({ nodeId, index, requisiteId: requisite.requisite_id }));
    },
    [dispatch, index, nodeId, requisite.requisite_id]
  );

  const handleDeleteRequisite = useCallback(
    () => deleteRequisite(nodeId, index, requisite.save_as),
    [deleteRequisite, nodeId, index, requisite.save_as]
  );

  const onKeyUp = useCallback(
    (e) => {
      preventClickThrough(e);
      if (isKeyEnter(e)) {
        dispatch(selectRequisite({ nodeId, index, requisiteId: requisite.requisite_id }));
      } else if (e.key === 'Delete') {
        handleDeleteRequisite();
      }
    },
    [dispatch, nodeId, index, requisite.requisite_id, handleDeleteRequisite]
  );

  const [{ canDrop }] = useDrop({
    accept: TYPES.QUESTION,
    drop: dropRequisiteInNode,
    canDrop: () => {
      return true;
    },
    collect: (monitor) => ({ canDrop: monitor.canDrop() }),
  });

  const isSelected = nodeId === selectedNodeId && index === selectedRequisiteIndex;
  return (
    <>
      <section
        id={requisite.requisite_id}
        className={cn(styles.action, { [styles['action--placeholder']]: canDrop })}
      >
        <div className={cn(styles.icon)}>
          {hasDialogError ? <BoxErrorIcon /> : hasDialogWarning ? <BoxWarningIcon /> : null}
        </div>

        <Box
          ref={(node) => {
            dragRef(node);
          }}
          className={cn({
            [styles.selected]: isSelected,
            [styles.selectedWarning]: hasDialogWarning && isSelected,
            [styles.selectedError]: isSelected && hasDialogError,
            [styles.opacity]: !isActionInFlow && !isAnythingSelected,
          })}
          role="button"
          tabIndex={0}
          onKeyUp={onKeyUp}
          onClick={onClick}
        >
          <ActionHeader
            type={type}
            onDelete={handleDeleteRequisite}
            objectToCopy={requisite}
            isSelected={isSelected}
            hasDialogError={hasDialogError}
          />
          <div className={styles.action__content}>
            <span className="text-sm leading-5 font-normal text-muted-foreground">
              {t('dialog.question.save_as', { 0: requisite?.save_as })}
            </span>
          </div>
        </Box>
      </section>
      <Placeholder
        nodeId={nodeId}
        requisiteIndex={index + 1}
        requisiteId={requisite.requisite_id}
      />
    </>
  );
};

export default Requisite;
